import * as React from 'react'

import { observer } from 'mobx-react'

import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import Select from 'modules/core/components/Inputs/Select'
import i18n from 'modules/core/lib/i18n'

import type AllowanceForm from 'modules/timeoff/lib/AllowanceForm'
import { sourceUnitsOptionsLabel } from 'modules/timeoff/lib/dynamicTranslations'
import VisibleLeaveTypes from 'modules/timeoff/lib/visibleLeaveTypes'
import leaveTypes from 'modules/timeoff/store/collections/leaveTypes'
import Allowance from 'modules/timeoff/store/models/Allowance'
import { ALL_SET_LEAVE_NAMES } from 'modules/timeoff/store/models/LeaveType'

interface Props {
  allowance: Allowance
  allowanceForm: AllowanceForm
  hasOvertimeTimeOff: boolean
  hasAccruedPaidTimeOff: boolean
  disabled: boolean
}

const AllowanceSourceUnitsSelect = ({
  allowance,
  allowanceForm,
  hasAccruedPaidTimeOff,
  hasOvertimeTimeOff,
  disabled,
}: Props) => {
  const visibleLeaveTypes = VisibleLeaveTypes(allowance, leaveTypes.active)

  const sourceUnitsLabel = i18n.t('time_off.allowances.page.labels.source_units')

  const sourceUnitsTooltip = i18n.t('time_off.allowances.page.information.source_units')

  const onlyBaseUnitsOption = [
    {
      label: sourceUnitsOptionsLabel(Allowance.SOURCE_UNITS.base_units),
      value: Allowance.SOURCE_UNITS.base_units,
    },
  ]

  const onlyOvertimeOptions = [
    ...onlyBaseUnitsOption,
    {
      label: sourceUnitsOptionsLabel(Allowance.SOURCE_UNITS.overtime_units),
      value: Allowance.SOURCE_UNITS.overtime_units,
    },
  ]

  const onlyAccruedPaidTimeOffOptions = [
    ...onlyBaseUnitsOption,
    {
      label: sourceUnitsOptionsLabel(Allowance.SOURCE_UNITS.by_worked_time),
      value: Allowance.SOURCE_UNITS.by_worked_time,
    },
  ]

  const allSourceUnitsOptions = Object.keys(Allowance.SOURCE_UNITS).map((value) => ({
    label: sourceUnitsOptionsLabel(value),
    value,
  }))

  // Options based on the feature flags enabled
  const onlyBaseUnits = !hasOvertimeTimeOff && !hasAccruedPaidTimeOff
  const onlyOvertime = hasOvertimeTimeOff && !hasAccruedPaidTimeOff
  const onlyAccruedPaidTimeOff = !hasOvertimeTimeOff && hasAccruedPaidTimeOff

  const options = () => {
    if (onlyBaseUnits) return onlyBaseUnitsOption

    if (onlyOvertime) return onlyOvertimeOptions

    if (onlyAccruedPaidTimeOff) return onlyAccruedPaidTimeOffOptions

    return allSourceUnitsOptions
  }

  const overtimeLeaveType = visibleLeaveTypes.find(
    (leaveType) => leaveType.attributes.identifier === ALL_SET_LEAVE_NAMES.overtime_compensation
  )

  const onChange = () => {
    allowanceForm.toggleSourceUnitsOptions(overtimeLeaveType?.attributes.id)
  }

  return (
    <Select
      field={allowanceForm.get('source_units')}
      label={sourceUnitsLabel}
      options={options()}
      secondaryLabel={<InformationTooltip information={sourceUnitsTooltip} icon="question" />}
      readonly={onlyBaseUnits || disabled}
      onChange={onChange}
    />
  )
}

export default observer(AllowanceSourceUnitsSelect)
